import React, {
  useEffect,
  useState,
  useMemo,
  useImperativeHandle,
  useCallback
} from "react";
import {
  DropzoneOptions,
  useDropzone
} from "react-dropzone";
import { Storage } from "aws-amplify";
import * as moment from "moment-timezone";
import * as _ from "lodash";
import { toast } from "react-toastify";
import styled, { css } from "styled-components";

import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text/Text";
import { Icons } from "src/library/components/Icon/types";
import DropZoneContainer, { DropZoneContainerType } from "src/core/shared/components/dropzones/DropZoneContainer";
import ListFiles, { FileData } from "src/core/shared/components/files/ListFiles";
import { Button } from "src/library/components/Button";

export interface DropZoneRef {
  openFileBrowser: () => void;
  clearFilesData: () => void;
}

interface DropZoneMultiFileProps {
  updateFilesToSave: (filesData: FileData[]) => void;
  dropzoneMessage?: string;
  dropzoneTitle?: string;
  s3Path?: string;
  type?: DropZoneContainerType;
  children?: React.ReactNode;
}

enum UploadStateTypes {
  IDLE = "idle",
  PENDING = "pending",
  SUCCESS = "success",
  ERROR = "error"
}

/**
 * Based on DropZoneContainerType, renders a dropzone component that saves files to a temporary s3 folder
 * and updates updateFilesToSave with keys.
 * Type defaulted to "informationView"
 */
const DropZoneMultiFile: React.ForwardRefRenderFunction<DropZoneRef, DropZoneMultiFileProps> = ({
  updateFilesToSave,
  dropzoneMessage = "",
  dropzoneTitle = "",
  s3Path = "tmp",
  type = "informativeView",
  children
}, ref) => {
  const [ uploadState, setUploadState ] = useState<UploadStateTypes>(UploadStateTypes.IDLE);
  const [ filesData, setFilesData ] = useState<FileData[]>([]);

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploadState(UploadStateTypes.PENDING);

    for (const file of acceptedFiles) {
      if (file.type.split("/")[ 0 ] === "video") {
        toast.error("Can not save video file types. Please try images");
        setUploadState(UploadStateTypes.ERROR);
        break;
      }

      const key = `${s3Path}/${moment.utc().format("DD-MM-YYYY:HH:mm:ss")}--${file.name}`;

      try {
        await Storage.put(key, file, {
          level: "public",
          contentType: file.type
        });

        const fileData = {
          s3FileKey: `public/${key}`,
          name: file.name
        };

        if (_.findIndex(filesData, file => file.name === fileData.name) === -1) {
          setFilesData(currentFileData => [ ...currentFileData, fileData ]);
          setUploadState(UploadStateTypes.SUCCESS);
        }
      } catch (error) {
        setUploadState(UploadStateTypes.ERROR);
        toast.error(`Could not upload file. Error: ${error}`);
      }
    }
  }, [ filesData, s3Path ]);

  useEffect(() => {
    updateFilesToSave(filesData);
  }, [ filesData, updateFilesToSave ]);

  const dropzoneOptions = useMemo<DropzoneOptions>(() => {
    const options: DropzoneOptions = {
      onDrop,
      noClick: true,
      noKeyboard: true,
      accept: [
        "image/png", // .png
        "image/jpeg", // .jpg, .jpeg
        "application/pdf", // .pdf
        "text/csv", // .csv
        "application/msword", // .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
        "application/vnd.ms-excel", // .xls
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        "application/vnd.ms-powerpoint", // .ppt
        "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx,
        "application/vnd.ms-outlook", // .msg
        ".msg" // .msg
      ]
    };

    if (type === "clearView") {
      options.noClick = false;
      options.noKeyboard = false;
    }

    return options;
  }, [ type, onDrop ]);

  const {
    getRootProps, getInputProps, isDragActive, open
  } = useDropzone(dropzoneOptions);

  useImperativeHandle(ref, () => ({
    openFileBrowser: () => {
      open();
    },
    clearFilesData: () => {
      setFilesData([]);
    }
  }));

  const renderContent = useCallback(() => {
    const removeFile = (file: FileData) => {
      setFilesData(currentFilesData => _.remove(
        currentFilesData, fileDataItem => fileDataItem.name !== file.name
      ));
    };

    switch (type) {
      case "clearView": {
        return (
          <>
            <Icon
              icon={Icons.DOWNLOAD}
              $fillColor="primaryDark"
            />

            <Text as="h4">
              {dropzoneMessage}
            </Text>
          </>
        );
      }
      case "informativeView": {
        return (
          <>
            <InformativeViewHeaderWrapper>
              <Text as="h4">
                {dropzoneTitle}
              </Text>

              <Button
                icon={Icons.ATTACH}
                iconPosition="right"
                $buttonType="primary"
                onClick={() => open()}
              >
                Attach Files
              </Button>
            </InformativeViewHeaderWrapper>

            <Text>
              {dropzoneMessage}
            </Text>

            {filesData && filesData.length > 0 && (
              <ListFiles
                files={filesData}
                onFileClick={removeFile}
              />
            )}
          </>
        );
      }
      case "transparentView": {
        return (
          <>
            {children}

            <ListFiles
              files={filesData}
              onFileClick={removeFile}
            />
          </>
        );
      }
      default: {
        return null;
      }
    }
  }, [
    children,
    dropzoneMessage,
    dropzoneTitle,
    filesData,
    open,
    type
  ]);

  return (
    <DropZoneContainer
      isDragActive={isDragActive}
      type={type}
    >
      <div {...getRootProps()}>
        <input {...getInputProps()} />

        {renderContent()}

        {uploadState === UploadStateTypes.PENDING && type !== "clearView" && (
          <LoadingSpinner
            type="flat"
            position="inline"
          />
        )}
      </div>
    </DropZoneContainer>
  );
};

const InformativeViewHeaderWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: ${theme.spacing.margins.md};

    h4 {
      margin-bottom: 0;
    }
    
    svg:hover {
      cursor: pointer;
    }
  `}
`;

export default React.forwardRef(DropZoneMultiFile);
